import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CalendarRange,
  Camera,
  CheckCircle2,
  Clock3,
  Languages,
  MonitorCog,
  MoonStar,
  ScanLine,
  Search,
  SlidersHorizontal,
  Smartphone,
  Store,
  SunMedium,
  UsersRound,
  Wifi,
  WifiOff,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router";
import { useAuth } from "../../lib/auth";
import { canAccessProvider } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { AppInstallLinks } from "../onboarding/AppInstallLinks";
import "./landing.css";

/* ── Sub-components ─────────────────────────────────────────── */

function LandingActionArrow() {
  const { direction } = useAppPreferences();
  const ArrowIcon = direction === "rtl" ? ArrowLeft : ArrowRight;

  return <ArrowIcon aria-hidden="true" className="ld-inline-icon" size={16} strokeWidth={2.25} />;
}

function LandingPreferenceControls() {
  const { language, setLanguage, setThemePreference, t, themePreference } = useAppPreferences();
  const nextLanguage = language === "ar" ? "en" : "ar";
  const nextThemePreference = themePreference === "dark" ? "light" : "dark";
  const themeToggleLabel =
    themePreference === "dark"
      ? t("preferences.theme.toggleToLight")
      : t("preferences.theme.toggleToDark");
  const ThemeIcon = themePreference === "dark" ? SunMedium : MoonStar;

  return (
    <div aria-label={t("preferences.title")} className="ld-pref-cluster">
      <button
        aria-label={language === "ar" ? t("preferences.language.toggleToEn") : t("preferences.language.toggleToAr")}
        className="ld-pref-toggle"
        onClick={() => setLanguage(nextLanguage)}
        title={language === "ar" ? t("preferences.language.toggleToEn") : t("preferences.language.toggleToAr")}
        type="button"
      >
        <span aria-hidden="true" className="ld-pref-toggle-icon">
          <Languages size={16} strokeWidth={2.2} />
        </span>
        <span aria-hidden="true" className="ld-pref-toggle-badge">{nextLanguage.toUpperCase()}</span>
        <span className="ld-pref-toggle-copy">
          {t(nextLanguage === "en" ? "preferences.language.en" : "preferences.language.ar")}
        </span>
      </button>
      <button
        aria-label={themeToggleLabel}
        className="ld-pref-toggle"
        onClick={() => setThemePreference(nextThemePreference)}
        title={themeToggleLabel}
        type="button"
      >
        <span aria-hidden="true" className="ld-pref-toggle-icon">
          <ThemeIcon size={16} strokeWidth={2.2} />
        </span>
        <span className="ld-pref-toggle-copy">
          {t(nextThemePreference === "light" ? "preferences.theme.light" : "preferences.theme.dark")}
        </span>
      </button>
    </div>
  );
}

function Nav() {
  const { language, t } = useAppPreferences();
  const { user, homePath, status } = useAuth();
  const isAuthed = status === "authenticated" && !!user;
  const sections = [
    { href: "#for-venues", label: t("landing.nav.forVenues") },
    { href: "#platforms", label: t("landing.nav.platforms") },
    { href: "#how-it-works", label: t("landing.nav.howItWorks") },
  ];
  const orderedSections = language === "ar" ? [...sections].reverse() : sections;

  return (
    <nav className="ld-nav ld-nav-landing">
      <Link className="ld-logo" to="/">
        <span className="ld-logo-wordmark">GAAMED</span>
      </Link>
      <div className="ld-nav-links">
        {orderedSections.map((section) => (
          <a href={section.href} key={section.href}>{section.label}</a>
        ))}
      </div>
      <div className="ld-nav-end">
        <LandingPreferenceControls />
        {isAuthed ? (
          <Link className="ld-btn ld-btn-purple" to={homePath}>
            {t("landing.nav.goToWorkspace")} <LandingActionArrow />
          </Link>
        ) : (
          <>
            <Link className="ld-nav-ghost" to="/sign-in">
              {t("landing.nav.signIn")}
            </Link>
            <Link className="ld-btn ld-btn-ghost" to="/provider-register">
              {t("landing.nav.register")} <LandingActionArrow />
            </Link>
          </>
        )}
      </div>
    </nav>
  );
}

function Hero() {
  const { direction, t } = useAppPreferences();
  const { user, homePath, status } = useAuth();
  const isAuthed = status === "authenticated" && !!user;
  const floatingIcons = [
    { delay: "0s", left: "8%", size: "2rem", symbol: "🎮", top: "14%" },
    { delay: "0.8s", left: "72%", size: "1.6rem", symbol: "🕹️", top: "16%" },
    { delay: "1.4s", left: "18%", size: "1.4rem", symbol: "🎲", top: "52%" },
    { delay: "2s", left: "80%", size: "1.7rem", symbol: "⚡", top: "60%" },
    { delay: "2.8s", left: "12%", size: "1.5rem", symbol: "🏁", top: "78%" },
    { delay: "3.2s", left: "68%", size: "1.3rem", symbol: "✨", top: "82%" },
  ] as const;
  const stats: Array<{ bodyKey: string; icon: LucideIcon; titleKey: string }> = [
    { icon: Search, titleKey: "landing.hero.cards.bookings.title", bodyKey: "landing.hero.cards.bookings.body" },
    { icon: CalendarClock, titleKey: "landing.hero.cards.sessions.title", bodyKey: "landing.hero.cards.sessions.body" },
    { icon: Languages, titleKey: "landing.hero.cards.language.title", bodyKey: "landing.hero.cards.language.body" },
    { icon: Smartphone, titleKey: "landing.hero.cards.device.title", bodyKey: "landing.hero.cards.device.body" },
  ];
  const artCards: Array<{ bodyKey: string; icon: LucideIcon; titleKey: string }> = [
    { icon: Search, titleKey: "landing.hero.cards.bookings.title", bodyKey: "landing.hero.cards.bookings.body" },
    { icon: CalendarClock, titleKey: "landing.hero.cards.sessions.title", bodyKey: "landing.hero.cards.sessions.body" },
    { icon: Languages, titleKey: "landing.hero.cards.language.title", bodyKey: "landing.hero.cards.language.body" },
  ];

  return (
    <section className="ld-hero">
      <div className="ld-hero-inner">
        <div className="ld-hero-layout">
          <div className="ld-hero-copy" dir={direction}>
            <h1 className="ld-hero-headline">
              <span className="ld-hero-line">{t("landing.hero.title.book")}</span>
              <span className="ld-hero-line">{t("landing.hero.title.checkIn")}</span>
              <span className="ld-hero-line ld-lime">{t("landing.hero.title.play")}</span>
            </h1>
            <p className="ld-hero-sub">
              {t("landing.hero.body")}
            </p>
            <AppInstallLinks variant="landing" />
            <div className="ld-hero-ctas join-venue-links">
              {isAuthed ? (
                <Link className="ld-nav-ghost" to={homePath}>
                  {t("landing.nav.goToWorkspace")} <LandingActionArrow />
                </Link>
              ) : (
                <>
                  <Link className="ld-nav-ghost" to="/provider-register">
                    {t("landing.hero.register")} <LandingActionArrow />
                  </Link>
                  <Link className="ld-nav-ghost" to="/sign-in">
                    {t("landing.nav.signIn")}
                  </Link>
                </>
              )}
            </div>
            <div className="ld-stats">
              {stats.map(({ bodyKey, icon: Icon, titleKey }) => (
                <div className="ld-stat" key={titleKey}>
                  <span className="ld-stat-icon" aria-hidden="true">
                    <Icon size={18} strokeWidth={2.2} />
                  </span>
                  <span className="ld-stat-n">{t(titleKey)}</span>
                  <span className="ld-stat-l">{t(bodyKey)}</span>
                </div>
              ))}
            </div>
          </div>
          <div aria-hidden="true" className="ld-hero-art">
            <div className="ld-hero-art-surface">
              <div
                aria-hidden="true"
                className="ld-hero-art-image"
                style={{ backgroundImage: "url('/images/provider-ops-hero.png')" }}
              />
              {floatingIcons.map((item) => (
                <span
                  className="ld-hero-float"
                  key={`${item.symbol}-${item.left}-${item.top}`}
                  style={{
                    animationDelay: item.delay,
                    fontSize: item.size,
                    left: item.left,
                    top: item.top,
                  }}
                >
                  {item.symbol}
                </span>
              ))}
              <div className="ld-hero-art-panel">
                {artCards.map(({ bodyKey, icon: Icon, titleKey }) => (
                  <div className="ld-hero-art-chip" key={titleKey}>
                    <span className="ld-hero-art-chip-icon">
                      <Icon size={18} strokeWidth={2.2} />
                    </span>
                    <div className="ld-hero-art-chip-copy">
                      <span className="ld-hero-art-chip-title">{t(titleKey)}</span>
                      <span className="ld-hero-art-chip-body">{t(bodyKey)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useAppPreferences();
  const steps: Array<{ descKey: string; icon: LucideIcon; number: string; titleKey: string }> = [
    {
      number: "01",
      titleKey: "landing.how.steps.browse.title",
      descKey: "landing.how.steps.browse.desc",
      icon: Search,
    },
    {
      number: "02",
      titleKey: "landing.how.steps.book.title",
      descKey: "landing.how.steps.book.desc",
      icon: CalendarClock,
    },
    {
      number: "03",
      titleKey: "landing.how.steps.arrive.title",
      descKey: "landing.how.steps.checkIn.desc",
      icon: CheckCircle2,
    },
  ];

  return (
    <section className="ld-section" id="how-it-works">
      <span className="ld-eyebrow">{t("landing.how.eyebrow")}</span>
      <h2 className="ld-section-h">{t("landing.how.title.line1")}<br />{t("landing.how.title.line2")}</h2>
      <p className="ld-section-p">
        {t("landing.how.body")}
      </p>
      <div className="ld-steps">
        {steps.map((step) => (
          <div className="ld-step" key={step.number}>
            <span className="ld-step-ghost-n">{step.number}</span>
            <span className="ld-step-icon" aria-hidden="true">
              <step.icon size={20} strokeWidth={2.1} />
            </span>
            <span className="ld-step-index">{t("landing.how.stepLabel", { number: step.number })}</span>
            <h3 className="ld-step-title">{t(step.titleKey)}</h3>
            <p className="ld-step-desc">{t(step.descKey)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ForVenues() {
  const { t } = useAppPreferences();
  const features: Array<{ descKey: string; icon: LucideIcon; titleKey: string }> = [
    {
      titleKey: "landing.venues.features.sessions.title",
      descKey: "landing.venues.features.sessions.desc",
      icon: MonitorCog,
    },
    {
      titleKey: "landing.venues.features.calendar.title",
      descKey: "landing.venues.features.calendar.desc",
      icon: CalendarRange,
    },
    {
      titleKey: "landing.venues.features.qr.title",
      descKey: "landing.venues.features.qr.desc",
      icon: CheckCircle2,
    },
    {
      titleKey: "landing.venues.features.team.title",
      descKey: "landing.venues.features.team.desc",
      icon: UsersRound,
    },
    {
      titleKey: "landing.venues.features.offline.title",
      descKey: "landing.venues.features.offline.desc",
      icon: Wifi,
    },
    {
      titleKey: "landing.venues.features.policy.title",
      descKey: "landing.venues.features.policy.desc",
      icon: SlidersHorizontal,
    },
  ];

  return (
    <section className="ld-section" id="for-venues" style={{ paddingTop: 0 }}>
      <span className="ld-eyebrow">{t("landing.venues.eyebrow")}</span>
      <h2 className="ld-section-h">{t("landing.venues.title.line1")}<br />{t("landing.venues.title.line2")}</h2>
      <p className="ld-section-p">
        {t("landing.venues.body")}
      </p>
      <div className="ld-features">
        {features.map((f) => (
          <div className="ld-feature" key={f.titleKey}>
            <div className="ld-feature-head">
              <span className="ld-feature-icon" aria-hidden="true">
                <f.icon size={20} strokeWidth={2.1} />
              </span>
              <div className="ld-feature-bar" />
            </div>
            <h3 className="ld-feature-title">{t(f.titleKey)}</h3>
            <p className="ld-feature-desc">{t(f.descKey)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PlatformPortals() {
  const { t } = useAppPreferences();
  const { user, status } = useAuth();
  const isAuthed = status === "authenticated" && !!user;

  const portals: Array<{
    ctaKey: string;
    ctaTo: string | null;
    descKey: string;
    eyebrowKey: string;
    icon: LucideIcon;
    muted: boolean;
    titleKey: string;
  }> = [
    {
      eyebrowKey: "landing.platforms.provider.eyebrow",
      titleKey: "landing.platforms.provider.title",
      descKey: "landing.platforms.provider.desc",
      ctaKey: isAuthed && user && canAccessProvider(user.role)
        ? "landing.platforms.provider.cta.open"
        : "landing.platforms.provider.cta.signIn",
      ctaTo: isAuthed && user && canAccessProvider(user.role) ? "/provider" : "/sign-in",
      muted: false,
      icon: Store,
    },
    {
      eyebrowKey: "landing.platforms.players.eyebrow",
      titleKey: "landing.platforms.players.title",
      descKey: "landing.platforms.players.desc",
      ctaKey: "landing.platforms.players.cta",
      ctaTo: "/join",
      muted: false,
      icon: Smartphone,
    },
  ];

  return (
    <section className="ld-portals" id="platforms">
      <span className="ld-eyebrow">{t("landing.platforms.eyebrow")}</span>
      <h2 className="ld-section-h" style={{ marginBottom: "48px" }}>
        {t("landing.platforms.title.line1")}<br />{t("landing.platforms.title.line2")}
      </h2>
      <div className="ld-portals-grid">
        {portals.map((p) =>
          p.ctaTo ? (
            <Link className="ld-portal-card" key={p.titleKey} to={p.ctaTo}>
              <div className="ld-portal-top">
                <span className="ld-portal-eyebrow">{t(p.eyebrowKey)}</span>
                <span className="ld-portal-icon" aria-hidden="true">
                  <p.icon size={20} strokeWidth={2.1} />
                </span>
              </div>
              <h3 className="ld-portal-title">{t(p.titleKey)}</h3>
              <p className="ld-portal-desc">{t(p.descKey)}</p>
              <span className={`ld-portal-cta${p.muted ? " ld-portal-cta-muted" : ""}`}>
                {t(p.ctaKey)} {!p.muted ? <LandingActionArrow /> : null}
              </span>
            </Link>
          ) : (
            <div className="ld-portal-card" key={p.titleKey} style={{ cursor: "default" }}>
              <div className="ld-portal-top">
                <span className="ld-portal-eyebrow">{t(p.eyebrowKey)}</span>
                <span className="ld-portal-icon" aria-hidden="true">
                  <p.icon size={20} strokeWidth={2.1} />
                </span>
              </div>
              <h3 className="ld-portal-title">{t(p.titleKey)}</h3>
              <p className="ld-portal-desc">{t(p.descKey)}</p>
              <span className={`ld-portal-cta${p.muted ? " ld-portal-cta-muted" : ""}`}>
                {t(p.ctaKey)}
              </span>
            </div>
          )
        )}
      </div>
    </section>
  );
}

function CTABand() {
  const { t } = useAppPreferences();
  const { user, homePath, status } = useAuth();
  const isAuthed = status === "authenticated" && !!user;

  return (
    <div className="ld-cta-band">
      <div>
        <h2 className="ld-cta-band-hl">
          {isAuthed ? t("landing.cta.authed.title.line1") : t("landing.cta.guest.title.line1")}<br />
          {isAuthed ? t("landing.cta.authed.title.line2") : t("landing.cta.guest.title.line2")}
        </h2>
        <p className="ld-cta-band-sub">
          {isAuthed
            ? t("landing.cta.authed.body")
            : t("landing.cta.guest.body")}
        </p>
      </div>
      <div className="ld-cta-band-btns">
        {isAuthed ? (
          <Link className="ld-btn ld-btn-lime ld-btn-lg" to={homePath}>
            {t("landing.nav.goToWorkspace")} <LandingActionArrow />
          </Link>
        ) : (
          <>
            <Link className="ld-btn ld-btn-lime ld-btn-lg" to="/provider-register">
              {t("landing.cta.register")} <LandingActionArrow />
            </Link>
            <Link className="ld-btn ld-btn-ghost ld-btn-lg" to="/sign-in">
              {t("landing.nav.signIn")}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function Footer() {
  const { t } = useAppPreferences();
  const { user, homePath, status } = useAuth();
  const isAuthed = status === "authenticated" && !!user;

  return (
    <footer className="ld-footer">
      <div className="ld-footer-brand">
        <Link className="ld-logo" to="/">
          <span className="ld-logo-wordmark">GAAMED</span>
        </Link>
        <p className="ld-footer-tagline">
          {t("landing.footer.tagline")}
        </p>
        <p className="ld-footer-copy">© {new Date().getFullYear()} Gaamed. {t("landing.footer.rights")}</p>
      </div>
      <div className="ld-footer-cols">
        <div>
          <p className="ld-footer-col-title">{t("landing.footer.product")}</p>
          <div className="ld-footer-col-links">
            <a href="#how-it-works">{t("landing.nav.howItWorks")}</a>
            <a href="#for-venues">{t("landing.nav.forVenues")}</a>
            <a href="#platforms">{t("landing.nav.platforms")}</a>
            <a className="gap-2" dir="ltr" href="https://www.instagram.com/gaamed.eg" rel="noopener noreferrer" target="_blank">
              <Camera aria-hidden="true" size={16} /> Instagram
            </a>
          </div>
        </div>
        <div>
          <p className="ld-footer-col-title">{t("landing.footer.account")}</p>
          <div className="ld-footer-col-links">
            {isAuthed ? (
              <Link to={homePath}>{t("landing.nav.goToWorkspace")}</Link>
            ) : (
              <>
                <Link to="/sign-in">{t("landing.nav.signIn")}</Link>
                <Link to="/provider-register">{t("landing.nav.register")}</Link>
              </>
            )}
          </div>
        </div>
        <div>
          <p className="ld-footer-col-title">{t("landing.footer.legal")}</p>
          <div className="ld-footer-col-links">
            <Link to="/legal/terms">{t("landing.footer.terms")}</Link>
            <Link to="/legal/privacy">{t("landing.footer.privacy")}</Link>
            <Link to="/legal/refund">{t("landing.footer.refund")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ── Page ───────────────────────────────────────────────────── */

export function LandingPage() {
  return (
    <div className="ld-root">
      <Nav />
      <Hero />
      <ForVenues />
      <PlatformPortals />
      <HowItWorks />
      <CTABand />
      <Footer />
    </div>
  );
}
